import * as React from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn, DrawerSectionTitle } from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskDetailSectionProps {
  /** Full-width slot rendered under the header and above the body — e.g. an ErrorBanner. */
  readonly banner?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly icon: LucideIcon;
  readonly title: React.ReactNode;
  /** Renders a thin top divider separating this section from the previous one. Omit for the first section in the dialog. */
  readonly withTopDivider?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Flat (card-less) section wrapper for the Task Detail dialog — sections sit directly on the dialog
 * background, separated by a thin top border. Deliberately does not reuse `DrawerSection` (always card-wrapped).
 */
export function TaskDetailSection({
  banner,
  children,
  icon,
  title,
  withTopDivider = true,
}: TaskDetailSectionProps): React.JSX.Element {
  return (
    <section className={cn(withTopDivider && 'pt-4 border-t border-border/60')}>
      <DrawerSectionTitle className="mb-3" icon={icon} title={title} />
      {banner !== undefined && <div className="mb-3">{banner}</div>}
      {children}
    </section>
  );
}
